import { Grid } from '@mui/material';
import { getDownloadURL, ref, uploadBytesResumable } from 'firebase/storage';
import { useCallback, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { v4 as uuid } from 'uuid';

import useToast from 'hooks/useToast';
import { useUploadFileCabinetFileMutation } from 'redux-store/api/file-cabinet';
import { useAppDispatch } from 'redux-store/hooks/useAppDispatch';
import { useAppSelector } from 'redux-store/hooks/useAppSelector';
import { authSelector } from 'redux-store/selector';
import {
  addUpload,
  failUpload,
  finishUpload,
  updateUploadProgress,
} from 'redux-store/slices/file-cabinet-upload-slice';
import { storage } from 'utils/firebase';

type DragAndDropWrapperProps = {
  children: React.ReactNode;
  fromModal?: boolean;
  projectId?: number;
  projectFolderId?: number;
};

export const DragAndDropWrapper = ({
  children,
  fromModal,
  projectId,
  projectFolderId,
}: DragAndDropWrapperProps) => {
  const [isDragging, setIsDragging] = useState(false);

  const { folderId } = useParams();
  const { hash, pathname } = useLocation();

  const dispatch = useAppDispatch();
  const showToast = useToast();
  const { companyId } = useAppSelector(authSelector);

  const [postFileToCabinet] = useUploadFileCabinetFileMutation();

  const resolveFolderId = useCallback((): number => {
    if (folderId) return Number(folderId);
    if (fromModal && hash) return Number(hash.substring(1));
    if (fromModal && projectFolderId) return projectFolderId;

    return 0;
  }, [folderId, hash, fromModal, projectFolderId]);

  const uploadFileToFirebase = async ({
    file,
    tempId,
    folderId,
    projectId,
  }: {
    file: File;
    tempId: string;
    folderId: number;
    projectId?: number;
  }) => {
    try {
      const storagePath = `companies/${companyId}/projects/${projectId}/file-cabinet/${Date.now()}_${file.name}`;
      const storageRef = ref(storage, storagePath);
      const uploadTask = uploadBytesResumable(storageRef, file);

      await new Promise<void>((resolve, reject) => {
        uploadTask.on(
          'state_changed',
          (snapshot) => {
            const percent = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
            dispatch(updateUploadProgress({ tempId, progress: percent }));
          },
          (error) => reject(error),
          () => resolve(),
        );
      });

      const downloadUrl = await getDownloadURL(uploadTask.snapshot.ref);

      await postFileToCabinet({
        folderId,
        firebaseUrl: downloadUrl,
        name: file.name,
        mimeType: file.type,
        size: String(file.size),
        projectId,
      }).unwrap();

      dispatch(finishUpload({ tempId }));
    } catch {
      dispatch(failUpload({ tempId }));
      showToast({ type: 'error', message: 'Something went wrong!' });
    }
  };

  const handleFilesUpload = async (files: File[]) => {
    const parentFolderId = resolveFolderId();

    await Promise.all(
      files.map(async (file) => {
        const tempId = uuid();

        dispatch(
          addUpload({
            tempId,
            fileName: file.name,
            mimeType: file.type,
            size: file.size,
            progress: 0,
            status: 'uploading',
            projectId: Number(projectId),
            folderId: String(parentFolderId),
          })
        );

        await uploadFileToFirebase({
          file,
          tempId,
          folderId: parentFolderId,
          projectId,
        });
      })
    );
  };

  if (pathname.includes('file-cabinet') || !hash) {
    return <>{children}</>;
  }

  return (
    <Grid
      onDragOver={(e) => {
        e.preventDefault();
        setIsDragging(true);
      }}
      onDragLeave={(e) => {
        const nextTarget = e.relatedTarget as Node | null;
        if (!nextTarget || !e.currentTarget.contains(nextTarget)) {
          setIsDragging(false);
        }
      }}
      onDrop={(e) => {
        e.preventDefault();
        setIsDragging(false);

        const files = Array.from(e.dataTransfer.files);
        if (files.length) handleFilesUpload(files);
      }}
      sx={{
        width: '100%',
        height: 'calc(100% - 60px)',
        borderRadius: isDragging ? 2 : '0px',
        border: isDragging ? '2px dashed #1976d2' : '2px dashed transparent',
        transition: isDragging ? 'border 0.2s ease' : '',
      }}
    >
      {children}
    </Grid>
  );
};
